import { useContext } from 'react';
import {
  BIOMETRIC_ENROLLMENT_STATUS,
} from '../../../assets/hooks/useBiometricEnrollment';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { BiometricIcon } from '../../../assets/icons/icons';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './BiometricSecuritySheet.css';

function getEnabledCopy({ biometricLabel, biometric, common, t }) {
  return {
    title: biometric.enabledTitle,
    text: t('profile.biometric.enabledText', {
      label: biometricLabel,
      productName: common.appName,
    }),
    action: biometric.turnOff,
    disable: true,
  };
}

function getFinishSetupCopy({ biometricLabel, biometric, t }) {
  return {
    title: biometric.finishTitle,
    text: t('profile.biometric.finishText', { label: biometricLabel }),
    action: biometric.finishSetup,
    intent: 'finish_setup',
  };
}

function getEnableCopy(enrollment, { biometric, common, t }) {
  return {
    title: t('profile.biometric.enableTitle', { label: enrollment.biometricLabel }),
    text: t('profile.biometric.enableText', {
      label: enrollment.biometricLabel,
      productName: common.appName,
    }),
    action: t('profile.biometric.enableSpecific', { label: enrollment.biometricLabel }),
    intent: 'setup',
  };
}

function getSheetCopy(enrollment, translations) {
  const { biometric, common, t } = translations;
  const {
    activeAction,
    availability,
    biometricLabel,
    isBusy,
    status,
  } = enrollment;

  if (status === BIOMETRIC_ENROLLMENT_STATUS.SUCCESS) {
    return {
      title: biometric.enabledTitle,
      text: t('profile.biometric.enabledSuccessText', {
        label: biometricLabel,
        productName: common.appName,
      }),
      action: common.done,
      done: true,
    };
  }

  if (status === BIOMETRIC_ENROLLMENT_STATUS.DISABLED) {
    return {
      title: biometric.disabledTitle,
      text: t('profile.biometric.disabledAgainText', { label: biometricLabel }),
      action: common.done,
      done: true,
    };
  }

  if (status === BIOMETRIC_ENROLLMENT_STATUS.DENIED) {
    return {
      title: biometric.notEnabledTitle,
      text: biometric.notEnabledText,
      action: common.done,
      done: true,
    };
  }

  if (isBusy && activeAction === 'disable') {
    return getEnabledCopy({ biometricLabel, biometric, common, t });
  }

  if (isBusy && activeAction === 'finish_setup') {
    return getFinishSetupCopy({ biometricLabel, biometric, t });
  }

  if (isBusy && activeAction === 'check_permission') {
    return {
      title: biometric.checkPermissionTitle,
      text: t('profile.biometric.checkPermissionBusyText', { label: biometricLabel }),
      action: biometric.checkPermission,
      checkPermission: true,
    };
  }

  if (isBusy && activeAction === 'setup') {
    return getEnableCopy(enrollment, translations);
  }

  if (availability.isEnabled) {
    return getEnabledCopy({ biometricLabel, biometric, common, t });
  }

  if (availability.isTokenSaved && availability.shouldOpenSettings) {
    return {
      title: t('profile.biometric.permissionNeededTitle', { label: biometricLabel }),
      text: t('profile.biometric.permissionNeededText', { label: biometricLabel }),
      action: t('profile.biometric.openSettingsAction', { label: biometricLabel }),
    };
  }

  if (availability.shouldFinishSetup || status === BIOMETRIC_ENROLLMENT_STATUS.TOKEN_NEEDED) {
    return getFinishSetupCopy({ biometricLabel, biometric, t });
  }

  if (status === BIOMETRIC_ENROLLMENT_STATUS.CHECK_PERMISSION) {
    return {
      title: biometric.checkPermissionTitle,
      text: t('profile.biometric.checkPermissionBusyText', { label: biometricLabel }),
      action: biometric.checkPermission,
      checkPermission: true,
    };
  }

  if (status === BIOMETRIC_ENROLLMENT_STATUS.SETTINGS_NEEDED || availability.shouldOpenSettings) {
    return {
      title: t('profile.biometric.permissionNeededTitle', { label: biometricLabel }),
      text: t('profile.biometric.openSettingsText', {
        label: biometricLabel,
        productName: common.appName,
      }),
      action: t('profile.biometric.openSettingsAction', { label: biometricLabel }),
    };
  }

  if (status === BIOMETRIC_ENROLLMENT_STATUS.ERROR) {
    return {
      title: biometric.couldNotEnableTitle,
      text: biometric.couldNotEnableText,
      action: common.tryAgain,
    };
  }

  return getEnableCopy(enrollment, translations);
}

function BiometricSecuritySheet({ enrollment, onClose }) {
  const { common, profile, t } = useContext(TranslationContext);
  const copy = getSheetCopy(enrollment, {
    biometric: profile.biometric,
    common,
    t,
  });

  const handlePrimaryAction = async () => {
    if (copy.done) {
      onClose();
      return;
    }

    if (copy.disable) {
      await enrollment.disableEnrollment();
      return;
    }

    await enrollment.startEnrollment(
      copy.checkPermission
        ? { checkPermission: true, intent: 'check_permission' }
        : {
          intent: copy.intent || 'setup',
          useSettings: enrollment.status === BIOMETRIC_ENROLLMENT_STATUS.SETTINGS_NEEDED
            || enrollment.availability.shouldOpenSettings,
        }
    );
  };

  return (
    <BottomSheet
      className="biometric-security-sheet"
      panelClassName="biometric-security-sheet__panel"
      titleId="biometric-security-sheet-title"
      onClose={onClose}
    >
      <div className="biometric-security-sheet__content">
        <span className="biometric-security-sheet__icon" aria-hidden="true">
          <BiometricIcon className="biometric-security-sheet__icon-svg" />
        </span>
        <h2 id="biometric-security-sheet-title" className="biometric-security-sheet__title">
          {copy.title}
        </h2>
        <p className="biometric-security-sheet__text">{copy.text}</p>
      </div>

      <BottomActionButton
        className="biometric-security-sheet__action"
        onClick={handlePrimaryAction}
        disabled={enrollment.isBusy}
      >
        {enrollment.status === BIOMETRIC_ENROLLMENT_STATUS.DISABLING
          ? profile.biometric.turningOff
          : enrollment.isBusy
            ? profile.biometric.settingUp
            : copy.action}
      </BottomActionButton>
    </BottomSheet>
  );
}

export default BiometricSecuritySheet;
